import React, { useEffect, useState } from "react"
import { getAPIBaseURL } from "../../services/helpers"
import { getData } from "../../services/request"

const StatCard = ({ label, value, subtitle, icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-600">{label}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {icon && <div className="text-gray-400">{icon}</div>}
    </div>
  </div>
)

const StatusBreakdown = ({ title, data, colors, total }) => {
  const totalValue = total || Object.values(data).reduce((sum, val) => sum + (val || 0), 0)
  
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-xs text-gray-600">{key.replace(/_/g, ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-24 rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${totalValue > 0 ? (value / totalValue) * 100 : 0}%`,
                    backgroundColor: colors[key] || '#6B7280'
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-900">{value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const AdminReports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [reports, setReports] = useState(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const fetchReports = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/reports`
    const params = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    
    const res = await getData(url, {}, params)
    setLoading(false)
    
    if (res.error == "") {
      if (res.result.status === 200) {
        setReports(res.result.data?.data || null)
      } else {
        setError(res.result.data?.message || 'Failed to load reports')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchReports()
  }, [startDate, endDate])

  const handleDateFilter = () => {
    fetchReports()
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const requestStatusColors = {
    PENDING: '#F59E0B',
    PRICED: '#EAB308',
    PAID: '#10B981',
    PAYMENT_CONFIRMED: '#10B981',
    DELIVERED: '#3B82F6',
    RECEIVED: '#8B5CF6',
    CANCELLED: '#EF4444'
  }

  const paymentStatusColors = {
    pending: '#F59E0B',
    initiated: '#3B82F6',
    completed: '#10B981',
    failed: '#EF4444',
    cancelled: '#6B7280'
  }

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        
        {/* Date Filter */}
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="End Date"
          />
          {(startDate || endDate) && (
            <button
              onClick={handleClearFilter}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Loading reports...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          {error}
        </div>
      ) : !reports ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          No report data available
        </div>
      ) : (
        <>
          {/* User Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">User Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total Hospitals"
                value={reports.users.hospitals.total}
                subtitle={`${reports.users.hospitals.active} active, ${reports.users.hospitals.inactive} inactive`}
              />
              <StatCard
                label="Total Patients"
                value={reports.users.patients.total}
                subtitle={`${reports.users.patients.active} active, ${reports.users.patients.inactive} inactive`}
              />
              <StatCard
                label="Total Admins"
                value={reports.admins.total}
                subtitle={`${reports.admins.active} active, ${reports.admins.inactive} inactive`}
              />
              <StatCard
                label="Total Users"
                value={reports.users.hospitals.total + reports.users.patients.total}
                subtitle="Hospitals + Patients"
              />
            </div>
          </div>

          {/* Request Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Request Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard
                label="Total Requests"
                value={reports.requests.total}
              />
              <StatusBreakdown
                title="Requests by Status"
                data={reports.requests.byStatus}
                colors={requestStatusColors}
                total={reports.requests.total}
              />
            </div>
          </div>

          {/* Payment Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Payment Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total Payments"
                value={reports.payments.total}
              />
              <StatCard
                label="Completed Payments"
                value={reports.payments.byStatus.completed}
              />
              <StatCard
                label="Total Revenue"
                value={formatCurrency(reports.payments.revenue.total)}
                subtitle={reports.payments.revenue.currency}
              />
              <StatCard
                label="Average Payment"
                value={formatCurrency(reports.payments.revenue.average)}
                subtitle={reports.payments.revenue.currency}
              />
            </div>
            <div className="mt-4">
              <StatusBreakdown
                title="Payments by Status"
                data={reports.payments.byStatus}
                colors={paymentStatusColors}
                total={reports.payments.total}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Recent Activity (Last 7 Days)</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="New Hospitals"
                value={reports.recentActivity.hospitals}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Patients"
                value={reports.recentActivity.patients}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Requests"
                value={reports.recentActivity.requests}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Payments"
                value={reports.recentActivity.payments}
                subtitle="Last 7 days"
              />
            </div>
          </div>

          {/* Date Range Info */}
          {(reports.dateRange.startDate || reports.dateRange.endDate) && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
              <strong>Date Range:</strong>{" "}
              {reports.dateRange.startDate
                ? new Date(reports.dateRange.startDate).toLocaleDateString()
                : "Beginning"}{" "}
              to{" "}
              {reports.dateRange.endDate
                ? new Date(reports.dateRange.endDate).toLocaleDateString()
                : "Today"}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default AdminReports

